/**
 * Canvas-based image cropper utility for react-easy-crop.
 * Takes a source image URL and pixel-crop coordinates,
 * draws the cropped region onto a canvas, and returns a Blob.
 */

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new window.Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

/**
 * Crop the image to the given pixel area.
 * @param {string} imageSrc - DataURL or object URL of the source image
 * @param {Object} pixelCrop - { x, y, width, height } from react-easy-crop
 * @returns {Promise<Blob>} - The cropped image as a PNG Blob
 */
export default async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Output at the cropped dimensions (capped at 512 for avatar)
  const maxSize = 512;
  const outputSize = Math.min(pixelCrop.width, maxSize);

  canvas.width = outputSize;
  canvas.height = outputSize;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputSize,
    outputSize
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas crop failed'));
        return;
      }
      resolve(blob);
    }, 'image/png');
  });
}
